import React, { useContext, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { TextsContext } from '../../context/textsContext';
import DialogComponent from '../DialogComponent';
import { TitleH2Component } from '../TitleH2Component';
import NameFeedback, { NAME_FEEDBACK_STATUS } from './NameFeedback';
import SavingState from './SavingState';
import SavedState from './SavedState';
import SaveErrorState from './SaveErrorState';
import { useSaveFavoritePlan } from '../../hooks/myFavoritePlan/useSaveFavoritePlan';
import { useUpdateFavoritePlan } from '../../hooks/myFavoritePlan/useUpdateFavoritePlan';
import { useFavoritePlanNameExists } from '../../hooks/myFavoritePlan/useFavoritePlanNameExists';
import { validateNameLength, validateAlphanumeric } from '../../helpers/toolsHelper';
import { FAVORITE_PLAN_NAME_LENGTH } from '../../constants/validationConstants';
import style from '../../scss/saveFavoritePlan.module.scss';
import styleForm from '../../scss/form.module.scss';

const NAME_DEBOUNCE_MS = 500;

/**
 * Diálogo para guardar la cotización actual como plan favorito.
 * Está conectado al servicio (POST /my-favorite-plans) vía `useSaveFavoritePlan`.
 *
 * Estados (derivados de la mutación):
 *   form     → input con el nombre del plan (Save)
 *   loading  → mientras guarda; sin acciones ni "X" (no se puede cerrar)
 *   success  → guardado ok; sólo se cierra (y redirige al dashboard) cuando
 *              el usuario hace click en "Go to dashboard" o en la "X"
 *   error    → falló; permite reintentar (Retry / Cancel)
 *
 * Al cerrarse se reinicia el estado (input + mutación).
 *
 * El body enviado es `{ description, data }`: `description` es el nombre que escribe
 * el usuario y `data` es el request de `/rate-engine/compute` ya construido por quien
 * usa el diálogo, con el estado del formulario dentro de `contextData`.
 *
 * En modo edición (`favoritePlanId` presente) el diálogo es el mismo, con una sola
 * diferencia visible: el input llega precargado con `initialName` y el usuario puede
 * editarlo. Al confirmar hace PATCH (update) en vez de POST (create).
 *
 * @param {boolean}  show
 * @param {function} onClose
 * @param {object}   planData       payload de pricing ya construido → `data`
 * @param {string}   suggestedName  nombre sugerido (placeholder)
 * @param {string}   initialName    nombre precargado en el input (edición)
 * @param {number|string} favoritePlanId  si viene, el diálogo actualiza ese plan
 */
const SaveFavoritePlanDialog = ({
    show,
    onClose,
    planData,
    suggestedName = '',
    initialName = '',
    favoritePlanId,
}) => {
    const { texts } = useContext(TextsContext);
    const navigate = useNavigate();
    const [name, setName] = useState(initialName);
    // Nombre "asentado" tras el debounce: es el único que se valida contra el back.
    const [debouncedName, setDebouncedName] = useState(initialName.trim());
    const saveMutation = useSaveFavoritePlan();
    const updateMutation = useUpdateFavoritePlan();

    // Con id => edición (PATCH); sin id => creación (POST). Se usan las mismas props
    // de estado (isLoading/isSuccess/isError/mutate/reset) en ambos casos.
    const isEdit = favoritePlanId != null;
    const mutation = isEdit ? updateMutation : saveMutation;

    const trimmedName = name.trim();
    const originalName = initialName.trim();
    // Editando, el nombre actual "ya existe" (es el de este mismo plan): no debe
    // marcarse como tomado ni consultarse al back mientras no se cambie.
    const isUnchangedName = isEdit && Boolean(originalName) && trimmedName === originalName;

    // Debounce del input: mientras el usuario escribe, `debouncedName` se queda atrás
    // y eso mismo es lo que mantiene el botón deshabilitado (ver `nameStatus`).
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedName(trimmedName), NAME_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [trimmedName]);

    // Nombre obligatorio, alfanumérico, entre 5 y 50 caracteres (MVC-2084 AC Case 1).
    const isValidNameLength = validateNameLength(trimmedName, FAVORITE_PLAN_NAME_LENGTH.MIN, FAVORITE_PLAN_NAME_LENGTH.MAX);
    const isValidNameChars = validateAlphanumeric(trimmedName);
    const isNameFormatValid = isValidNameLength && isValidNameChars;

    const nameExistsQuery = useFavoritePlanNameExists(debouncedName, {
        enabled: show && !isUnchangedName && isNameFormatValid,
    });

    /**
     * Estado de validación del nombre:
     *   idle           → input vacío, sin feedback
     *   invalidChars   → contiene caracteres no alfanuméricos
     *   invalidLength  → fuera del rango [5, 50] caracteres
     *   checking       → escribiendo (debounce pendiente) o consultando al back
     *   available      → nombre libre; habilita Save
     *   taken          → ya existe un plan con ese nombre
     *   failed         → no se pudo validar; se deja guardar y el POST decide
     */
    const nameStatus = !trimmedName ? NAME_FEEDBACK_STATUS.IDLE
        : !isValidNameChars ? NAME_FEEDBACK_STATUS.INVALID_CHARS
        : !isValidNameLength ? NAME_FEEDBACK_STATUS.INVALID_LENGTH
        : isUnchangedName ? NAME_FEEDBACK_STATUS.AVAILABLE
        : (trimmedName !== debouncedName || nameExistsQuery.isFetching) ? NAME_FEEDBACK_STATUS.CHECKING
            : nameExistsQuery.isError ? NAME_FEEDBACK_STATUS.FAILED
                : nameExistsQuery.data === true ? NAME_FEEDBACK_STATUS.TAKEN
                    : nameExistsQuery.data === false ? NAME_FEEDBACK_STATUS.AVAILABLE
                        : NAME_FEEDBACK_STATUS.CHECKING;

    const canSave = nameStatus === NAME_FEEDBACK_STATUS.AVAILABLE || nameStatus === NAME_FEEDBACK_STATUS.FAILED;

    const status = mutation.isSuccess ? 'success'
        : mutation.isError ? 'error'
            : mutation.isLoading ? 'loading'
                : 'form';

    /**
     * Sale al dashboard tras guardar. No hace falta limpiar la cache: el dashboard
     * refetchea lista y métricas al montar por `DASHBOARD_QUERY_CONFIG` (staleTime 0 +
     * refetchOnMount 'always'), así que el plan recién creado/editado aparece sin datos viejos.
     */
    const goToDashboard = () => {
        navigate('/dashboard');
    };

    const handleClose = () => {
        if (mutation.isLoading) return; // no se puede cerrar mientras guarda
        onClose?.();
        // Sólo se redirige si el plan llegó a guardarse (cerrar el form no navega).
        if (mutation.isSuccess) goToDashboard();
    };

    const handleSave = () => {
        if (!trimmedName || !canSave) return;
        // Body: { description, data }. Todo lo que no sea el nombre viaja dentro de `data`.
        // En edición se agrega el id para que la mutación haga PATCH sobre ese plan.
        mutation.mutate({
            ...(isEdit ? { id: favoritePlanId } : {}),
            description: trimmedName,
            data: planData,
        });
    };

    // Al abrir, precarga el nombre (vacío al crear, el actual al editar). Al cerrar,
    // reinicia el estado (input + mutación) para que reabrir empiece limpio.
    useEffect(() => {
        if (show) {
            setName(initialName);
            setDebouncedName(initialName.trim());
        } else {
            setName('');
            setDebouncedName('');
            mutation.reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    // En "loading" no se pasa ningún handler → el DialogComponent no muestra footer ni "X".
    const actionsByStatus = {
        form: {
            onConfirm: handleSave,
            onClose: handleClose,
            confirmText: isEdit
                ? (texts?.favorite_plan_update_button ?? 'Update')
                : (texts?.favorite_plan_save_button ?? 'Save'),
            // Sólo se habilita cuando el nombre terminó de validarse y está libre.
            confirmDisabled: !canSave,
        },
        loading: {},
        success: {
            onConfirm: handleClose,
            onClose: handleClose,
            confirmText: texts?.favorite_plan_save_go_to_dashboard ?? 'Go to dashboard',
        },
        error: {
            onConfirm: handleSave,
            onCancel: handleClose,
            onClose: handleClose,
            confirmText: texts?.favorite_plan_save_retry ?? 'Retry',
            cancelText: texts?.favorite_plan_save_cancel ?? 'Cancel',
        },
    };

    return (
        <DialogComponent show={show} width="440px" {...actionsByStatus[status]}>
            {status === 'form' && (
                <>
                    <Icon icon="mdi:star-box-outline" className={style.icon} />
                    <TitleH2Component styleGroup={`raleway color_primary ${style.title}`}>
                        {isEdit
                            ? (texts?.favorite_plan_update_title ?? 'Update My Favorite Plan')
                            : (texts?.favorite_plan_save_title ?? 'Save to My Favorite Plans')}
                    </TitleH2Component>

                    <div className={`${styleForm.form_group} text-start`}>
                        <label htmlFor="favoritePlanName" className={`${styleForm.labels} raleway color_primary_2`}>
                            <span className={styleForm.punto}>*</span>
                            {texts?.favorite_plan_name_field_label ?? 'Name of My Favorite Plan'}
                        </label>
                        <input
                            id="favoritePlanName"
                            type="text"
                            name="favoritePlanName"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className={`${styleForm.input_with_linemodal} w-100`}
                            placeholder={suggestedName
                                ? `${texts?.favorite_plan_save_suggested ?? 'Suggested Name'}: ${suggestedName}`
                                : (texts?.favorite_plan_save_placeholder ?? 'Name')}
                            maxLength={50}
                        />
                        <NameFeedback status={nameStatus} />
                    </div>
                </>
            )}

            {status === 'loading' && <SavingState isEdit={isEdit} />}

            {status === 'success' && <SavedState isEdit={isEdit} />}

            {status === 'error' && <SaveErrorState isEdit={isEdit} />}
        </DialogComponent>
    );
};

export default SaveFavoritePlanDialog;
